/**
 * Report how far through an article the reader got.
 *
 * Copies the measurement pattern `ArticleCompactHeader` already uses: coalesce into
 * one `requestAnimationFrame`, read `getBoundingClientRect()`, and keep everything
 * out of React state. Scroll fires far faster than an article should re-render.
 *
 * Progress is measured from how much of the body has passed the *bottom* of the
 * viewport, not from a scrollTop ratio — the latter reaches 100% with a full screen
 * still unread below the fold, which would make "finished reading" mean nothing.
 */

import { type RefObject, useEffect, useRef } from "react";

export type ScrollMilestone = 25 | 50 | 75 | 100;

const MILESTONES: readonly ScrollMilestone[] = [25, 50, 75, 100];

export function useScrollDepth(
  bodyRef: RefObject<HTMLElement | null>,
  key: string | null,
  onMilestone: (depth: ScrollMilestone) => void,
): void {
  const highest = useRef(0);
  const latest = useRef(onMilestone);
  useEffect(() => {
    latest.current = onMilestone;
  });

  useEffect(() => {
    const body = bodyRef.current;
    if (!body || key === null) return;

    highest.current = 0;
    let frame = 0;

    const paint = () => {
      frame = 0;
      const rect = body.getBoundingClientRect();
      if (rect.height <= 0) return;
      const seen = window.innerHeight - rect.top;
      const progress = Math.min(1, Math.max(0, seen / rect.height)) * 100;

      for (const milestone of MILESTONES) {
        if (progress < milestone || highest.current >= milestone) continue;
        // A high-water mark, so scrolling back up fires nothing and each milestone
        // is reported at most once per article.
        highest.current = milestone;
        latest.current(milestone);
      }
    };

    const schedule = () => {
      if (frame === 0) frame = requestAnimationFrame(paint);
    };

    // An article shorter than the viewport is complete the moment it arrives.
    paint();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      if (frame !== 0) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [bodyRef, key]);
}
